/**
 * Privacy-first analytics beacon (ported from the slidecoder theme).
 *
 * Posts {event_type, path, referrer, properties} to /api/v1/track on the
 * pagecoder.ai backend. The server hashes IP + UA with a daily-rotating
 * salt — no cookies, no localStorage, no fingerprinting.
 *
 * IMPORTANT: the endpoint is the ROOT-ABSOLUTE `/api/v1/track`, NOT this app's
 * `apiBase` (which resolves to `/ai-visibility-scan/api` and hits the aio-scan
 * backend on :4002). At the pagecoder.ai root, Caddy routes `/api/*` to the
 * rag-chat backend on :3000, which owns the analytics endpoint. Same-origin,
 * so no CORS and no credentials.
 *
 * Failures are silent: analytics must never break a user-facing flow.
 */

const ENDPOINT = '/api/v1/track';

type EventType = 'pageview' | 'cta_click';

let lastPageviewPath: string | null = null;

const send = (event_type: EventType, path: string, properties?: Record<string, unknown>): void => {
  const body = JSON.stringify({
    event_type,
    path,
    referrer: typeof document !== 'undefined' ? document.referrer || undefined : undefined,
    properties,
  });

  // sendBeacon is fire-and-forget and survives navigation. It must carry an
  // application/json Blob, else Fastify won't parse it into req.body.
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(ENDPOINT, blob)) return;
    }
  } catch { /* fall through to fetch */ }

  try {
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => { /* swallow */ });
  } catch { /* swallow */ }
};

/** Record a custom event for the current path. */
export const track = (event_type: EventType, properties?: Record<string, unknown>): void => {
  if (typeof window === 'undefined') return;
  send(event_type, window.location.pathname, properties);
};

/**
 * Record a pageview for the current path. Deduplicates against the previous
 * pageview path within the same SPA session, so React StrictMode's double
 * render (and repeated hash changes on the same pathname) fire only one beacon.
 */
export const trackPageview = (): void => {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (path === lastPageviewPath) return;
  lastPageviewPath = path;
  send('pageview', path);
};
